/// <amd-module name="VSSUI/Components/Intersection/Intersection" />
import * as React from "react";
import { createMergedRef } from "VSSUI/Util";
import { IIntersectionProps } from "./Intersection.Props";

export interface IIntersectionContext {
    readonly rootMargin: number;
    readonly root: HTMLElement;

    observe: (element: HTMLElement) => void;
    register: (callback: IntersectionObserverCallback) => void;
    unobserve: (element: HTMLElement) => void;
    unregister: (callback: IntersectionObserverCallback) => void;
}

// We need to monitor fine grained changes, especially when the list
// has horizontal scroll. You dont get 100% visible ever.
const defaultThreshold: number[] = [];
for (let index = 0; index <= 100; index++) {
    defaultThreshold.push(index / 100);
}

class IntersectionContextImpl implements IIntersectionContext {
    private callbacks: IntersectionObserverCallback[] = [];
    private observer?: IntersectionObserver;
    private pending: HTMLElement[] = [];

    public rootMargin: number = 0;
    public root: HTMLElement = document.body;

    public connect(root: HTMLElement, rootMargin: number = 0, threshold: number[] = defaultThreshold) {
        this.observer = new IntersectionObserver(this.onIntersect, { root, rootMargin: rootMargin + "px", threshold });
        this.rootMargin = rootMargin;
        this.root = root;

        for (const element of this.pending) {
            this.observer.observe(element);
        }
    }

    public disconnect() {
        if (this.observer) {
            this.observer.disconnect();
        }
    }

    public observe(element: HTMLElement) {
        if (this.observer) {
            this.observer.observe(element);
        } else {
            this.pending.push(element);
        }
    }

    public onIntersect = (entries: IntersectionObserverEntry[]) => {
        if (this.observer) {
            for (const callback of this.callbacks) {
                callback(entries, this.observer);
            }
        }
    };

    public register(callback: IntersectionObserverCallback) {
        this.callbacks.push(callback);
    }

    public unobserve(element: HTMLElement) {
        const elementIndex = this.pending.indexOf(element);
        if (elementIndex >= 0) {
            this.pending.splice(elementIndex, 1);
        }
        if (this.observer) {
            this.observer.unobserve(element);
        }
    }

    public unregister(callback: IntersectionObserverCallback) {
        const callbackIndex = this.callbacks.indexOf(callback);

        if (callbackIndex >= 0) {
            this.callbacks.splice(callbackIndex, 1);
        }
    }
}

export const IntersectionContext = React.createContext<IIntersectionContext>(new IntersectionContextImpl());

/**
 * The Intersection is used to observe the changes of visibility in the children
 * of the rootElement. It also will notify the caller when the rootElement is
 * scrolled. It will pass an empty array of entries in the scorlling case.
 */
export class Intersection extends React.Component<IIntersectionProps, IIntersectionContext> {
    private externalElement: HTMLElement | null;
    private mergedRef = createMergedRef<HTMLElement>();
    private rootElement = React.createRef<HTMLElement>();

    public state = new IntersectionContextImpl();

    // Render the provider around a SINGLE child. This is the element that is scrollable.
    public render(): JSX.Element {
        const child = React.Children.only(this.props.children) as React.DetailedReactHTMLElement<any, HTMLElement>;

        let onScroll: (event: React.SyntheticEvent<HTMLElement>) => void | undefined;
        if (child.props.onScroll) {
            onScroll = (event: React.SyntheticEvent<HTMLElement>) => {
                if (child.props.onScroll) {
                    child.props.onScroll(event);
                }

                this.onScroll(event);
            };
        } else {
            onScroll = this.onScroll;
        }

        return (
            <IntersectionContext.Provider value={this.state}>
                {React.cloneElement(
                    child,
                    {
                        ...child.props,
                        ref: this.mergedRef(
                            this.rootElement,
                            child.ref as React.Ref<HTMLElement>
                        ),
                        onScroll,
                    },
                    child.props.children
                )}
            </IntersectionContext.Provider>
        );
    }

    public componentDidMount() {
        const { observationElement, rootElement } = this.props;
        let connectElement: HTMLElement | null = null;

        if (rootElement) {
            if (typeof rootElement === "string") {
                connectElement = document.querySelector(rootElement);
            } else if (typeof rootElement === "function") {
                connectElement = rootElement();
            } else {
                connectElement = rootElement;
            }

            if (connectElement) {
                connectElement.addEventListener("scroll", this.onScroll);
                this.externalElement = connectElement;
            }
        } else if (this.rootElement) {
            connectElement = this.rootElement.current;
        }

        if (connectElement) {
            this.state.connect(connectElement, this.props.rootMargin, this.props.threshold);

            // Allow the creator of the intersection to observe intersection events.
            if (this.props.onIntersect) {
                this.state.register(this.props.onIntersect);
            }

            if (observationElement) {
                let observeElement: HTMLElement | null;

                if (typeof observationElement === "string") {
                    observeElement = document.querySelector(observationElement);
                } else if (typeof observationElement === "function") {
                    observeElement = observationElement();
                } else {
                    observeElement = observationElement;
                }

                if (observeElement) {
                    this.state.observe(observeElement);
                }
            }
        }
    }

    public componentWillUnmount() {
        if (this.externalElement) {
            this.externalElement.removeEventListener("scroll", this.onScroll);
        }

        this.state.disconnect();
    }

    private onScroll = (event: React.SyntheticEvent<HTMLElement> | Event) => {
        this.state.onIntersect([]);
    };
}
